/**
 * rother3 companion — the shared engine behind all seven comparison pages.
 *
 * ## The layout decision this file exists to make
 *
 * The old comparison puts **fields down the side and businesses across the top**: 26 rows ×
 * 137 columns. That shape is good at four businesses and impossible at a hundred. It also fails
 * in a way that is hard to see — the answer to "which of these 137 competitors has no phone
 * number" is a single horizontal scan, and the scan is 4,000 pixels wide.
 *
 * So every page here is **business-major**: one row per business, one column per field. The
 * same 26 rows become 26 columns, and 137 businesses become 137 rows. Scanning for "who has no
 * phone" is now a vertical read of one column, which is the thing human eyes are best at and
 * the thing a spreadsheet does.
 *
 * ## What that costs, stated rather than discovered later
 *
 * A field-major table answers "how does business B compare to A on *this one field*" by
 * looking across. A business-major table answers "which businesses look like *this*" by looking
 * down. These are different questions and both are real. The answer is not to pick one — it is
 * to keep the ranking charts, which carry the across view, on the same page. That is why the
 * page renders a leader board beside the table rather than instead of it.
 *
 * ## Why paged, and not virtualized
 *
 * 137 businesses × 26 fields is 3,562 cells. Rendering all of them is survivable; rendering
 * them, then re-rendering on every keystroke of the filter box, is not. So rows are paged at
 * 25, the page count is stated ("showing 1–25 of 137"), and the filter runs over the *whole*
 * set before paging — a reader searching for one business finds it on any page rather than
 * having to guess which page it landed on.
 *
 * Virtualization would scroll continuously and destroy exactly what makes a table usable: a
 * sticky header row and a sticky first column. Paging keeps both, works with a keyboard, and
 * announces correctly to a screen reader.
 */
import { escapeHtml, escapeAttribute } from './html.js';
import { CELL } from './compare-rows.js';
import { count } from './format.js';

/** Rows per page. Chosen against the page height, not aesthetics. */
export const PAGE_SIZE = 25;

/**
 * Turn one cell into the text a table shows.
 *
 * ## Returns `''` for anything unreadable, and that is load-bearing
 *
 * An unreadable cell renders a dash **with the reason**, never a value and never a blank that
 * reads as zero. This is the `compare-rows.js` rule stated for the other orientation: one gap
 * is not two, and a blank cell in a column of numbers reads as a number nobody published.
 */
export function formatCellText(cellValue, format) {
  if (!cellValue || !cellValue.readable) {return '';}
  const v = cellValue.value;

  switch (format) {
    case 'number':
      return typeof v === 'number' ? count(v) : '';
    /**
     * A star rating is **not** a count.
     *
     * `count()` rounds, because `count()` is for counts — `2,074` reviews, `156` photos — and a
     * count has no meaningful decimals. A rating does: `4.7` rounded is `5`, which is not a
     * different rating of the same business, it is a *different rating the business does not
     * have*. The screenshot showed a table of five businesses, every one reading exactly `5`.
     *
     * One decimal, always. `4.0` is `4.0` rather than `4` so a column of ratings aligns on the
     * decimal point and a reader is not left wondering whether `4` means 4.0 or was truncated.
     */
    case 'rating':
      return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(1) : '';
    case 'percent':
      return typeof v === 'number' ? `${round(v, 1)}%` : '';
    case 'flag':
      return v === true ? '✓ yes' : v === false ? '— no' : '';
    case 'list':
      return Array.isArray(v) ? (v.length > 0 ? v.join(', ') : '—') : '';
    case 'hours':
    case 'text':
      return typeof v === 'string' ? v : '';

    case 'object': {
      if (v === null || typeof v !== 'object') {return '';}
      // The shapes below are matched in the order a reader is most likely to want them, and
      // each is unambiguous — no shape is a subset of another's fields, so the order is a
      // preference rather than a correctness requirement.
      if (typeof v.count === 'number' && typeof v.share === 'number') {
        return `${count(v.count)} (${round(v.share, 1)}%)`;
      }
      if (typeof v.count === 'number' && typeof v.of === 'number') {
        return `${v.count} of ${v.of}`;
      }
      if (typeof v.count === 'number' && typeof v.percent === 'number') {
        return `${v.count} (${round(v.percent, 1)}%)`;
      }
      if (typeof v.chars === 'number') {
        return v.words === 1 ? `${v.chars} char` : `${v.chars} chars · ${v.words} words`;
      }
      if (typeof v.text === 'string') {
        return v.day ? `${v.text} (${v.day})` : v.text;
      }
      if (typeof v.captured === 'number') {
        return typeof v.of === 'number' ? `${v.captured} of ${count(v.of)}` : `${v.captured}`;
      }
      if (typeof v.read === 'number' && typeof v.of === 'number') {
        return `${v.read} of ${v.of}`;
      }
      if (typeof v.groups === 'number') {
        return typeof v.items === 'number'
          ? `${v.groups} groups · ${v.items} items`
          : `${v.groups} groups`;
      }
      if (typeof v.total === 'number') {return count(v.total);}
      if (Array.isArray(v.names) && v.names.length > 0) {return v.names.join(', ');}
      if (typeof v.count === 'number') {return count(v.count);}
      return '';
    }
    default:
      return '';
  }
}

function round(n, places) {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}

/** Why a cell is blank, in words. Screen readers read it; sighted readers get it as a title. */
export function cellStateReason(cellValue) {
  if (!cellValue || cellValue.readable) {return '';}
  switch (cellValue.state) {
    case CELL.notPublished:
      return 'Maps publishes none';
    case CELL.unread:
      return 'not read';
    case CELL.unknown:
      return 'not in this export';
    default:
      return 'not read';
  }
}

/**
 * The flat column list for a family: one column per field, in section order.
 *
 * Section boundaries are carried through as a `sectionIndex`, because a table with 26 columns
 * and no visible grouping is 26 undifferentiated columns. The header prints the section name
 * once, spanning its columns.
 */
export function buildColumns(sections) {
  const columns = [];
  sections.forEach((section, sectionIndex) => {
    for (const row of section.rows) {
      columns.push({
        key: row.key,
        label: row.label,
        hint: row.hint ?? '',
        format: row.format,
        better: row.better,
        /**
         * The width this column wants, decided by what it holds rather than by one number for
         * every column. See `COLUMN_WIDTH` — a rating column that clips `4.7` to `5` is worse
         * than one that is 40px too wide.
         */
        width: typeof row.width === 'number' ? row.width : 140,
        sectionIndex,
        sectionTitle: section.title,
        reader: row.cells,
      });
    }
  });
  return columns;
}

/**
 * One row per business.
 *
 * `columns[i].reader` is the per-business cell array for that field, so building a row is a
 * transpose: for each business, walk the columns and take cell `[businessIndex]`.
 */
export function buildBusinessRows(fields, columns) {
  return fields.map((f, index) => ({
    index,
    placeId: typeof f?.placeId === 'string' ? f.placeId : null,
    name: typeof f?.businessName === 'string' && f.businessName.trim() !== ''
      ? f.businessName.trim()
      : `Business ${index + 1}`,
    anchor: index === 0,
    cells: columns.map((c) => ({
      key: c.key,
      format: c.format,
      better: c.better,
      state: c.reader[index],
    })),
    /** Everything searchable in one string, built once so filtering is not per-keystroke work. */
    haystack: [f?.businessName, f?.placeId, f?.address, f?.phone, f?.website,
      Array.isArray(f?.categories) ? f.categories.map((c) => c?.name).join(' ') : '']
      .filter((s) => typeof s === 'string')
      .join(' ')
      .toLowerCase(),
  }));
}

/**
 * Filter, sort and page, in that order, with the anchor pinned.
 *
 * ## The anchor is pinned, and the count says so
 *
 * "Your business" is the row the page exists to compare everything else against, so it stays
 * on every page regardless of the filter. That is a real consequence of the choice and the
 * count reflects it: `pinned + matching of N`. A reader who filtered to one competitor and
 * sees two rows knows why.
 *
 * ## The one exception: a filter that matched nothing
 *
 * Pinning is skipped when the filter matched **zero** businesses. A reader who typed a search
 * and got an unrelated row back would conclude the filter is broken, when in fact it worked and
 * the answer is "no such business". Saying nothing matched is the only honest response, and it
 * is the difference between a search box that reports its own results and one that appears to
 * ignore you.
 */
export function applyView(rows, options = {}) {
  const { filter = '', sort = 'name', direction = 'asc', page = 1, pageSize = PAGE_SIZE } = options;

  const anchorRow = rows.filter((r) => r.anchor);
  const rest = rows.filter((r) => !r.anchor);

  const needle = filter.trim().toLowerCase();
  const matchedRest = needle === '' ? rest : rest.filter((r) => r.haystack.includes(needle));

  // A filter that matched nothing suppresses the pin, so the empty state is reachable.
  const suppressPin = needle !== '' && matchedRest.length === 0;
  const anchor = suppressPin ? [] : anchorRow;
  const matched = matchedRest;

  const sorted = [...matched].sort(comparator(sort, direction));
  const ordered = [...anchor, ...sorted];
  const total = ordered.length;

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  const visible = ordered.slice(start, start + pageSize);

  return {
    visible,
    total,
    matched: matched.length,
    pinned: anchor.length,
    /** Whether a filter is doing the selecting, which is what makes the pin worth explaining. */
    filtered: needle !== '',
    page: safePage,
    pageCount,
    pageSize,
    firstShown: total === 0 ? 0 : start + 1,
    lastShown: Math.min(start + pageSize, total),
  };
}

/** A sort by name, or by the value of a named column. Unknown keys fall back to name. */
function comparator(sort, direction) {
  const sign = direction === 'desc' ? -1 : 1;

  if (sort === 'name') {
    return (a, b) => sign * a.name.localeCompare(b.name);
  }

  return (a, b) => {
    const left = numericFor(a, sort);
    const right = numericFor(b, sort);
    // Unreadable sorts last in **both** directions. A business rother3 could not read is not
    // "the worst" and is not "the best"; putting it at either end of a descending sort is a
    // claim the data does not support.
    if (left === null && right === null) {return a.name.localeCompare(b.name);}
    if (left === null) {return 1;}
    if (right === null) {return -1;}
    if (left === right) {return a.name.localeCompare(b.name);}
    return sign * (left - right);
  };
}

/** The sortable number behind a column, or `null` when the cell is unreadable. */
function numericFor(row, key) {
  const cellValue = row.cells.find((c) => c.key === key);
  if (!cellValue || !cellValue.state?.readable) {return null;}
  const v = cellValue.state.value;
  if (typeof v === 'number') {return v;}
  if (v && typeof v === 'object' && typeof v.count === 'number') {return v.count;}
  if (v && typeof v === 'object' && typeof v.rating === 'number') {return v.rating;}
  return null;
}

/**
 * The figure a leader board prints beside a name.
 *
 * Not `formatCellText`, because a leader board needs the **number** while a cell shows the whole
 * value. "Missing checklist items" formats as `hasLogo` in a cell — the names are the finding,
 * and they are what let a reader check the count. In a leader board that column then rendered
 * every entry as the same word, so ten businesses were ranked by a figure none of them showed.
 *
 * The number comes from `numericFor`, the same function the sort uses, so a leader board cannot
 * rank by a different measure than the table sorts by.
 */
function leaderValue(row, column) {
  const n = numericFor(row, column.key);
  if (n === null) {return '';}
  // Same distinction as `formatCellText`: a rating is not a count, and `count()` would round
  // 4.7 to 5 and make every business in the board look identical.
  if (column.format === 'rating') {return n.toFixed(1);}
  return column.format === 'percent' ? `${round(n, 1)}%` : count(n);
}

/**
 * The formats that hold a number a reader could sort or rank by.
 *
 * ## Why this is one exported list rather than a literal at each use
 *
 * It was written out twice — once in `sortableColumns`, once in `compare-app.js`'s sort
 * `<select>` — and adding the `rating` format to only one of them silently removed Rating from the
 * sort dropdown and from every leader board, with no error anywhere. The two lists disagreed and
 * nothing noticed, which is the exact failure mode this file is careful about elsewhere.
 *
 * `rating` is here because a star rating is a perfectly good sort key. It is *not* `number`,
 * because `number` means "a count", and `count()` rounds — which is how a table of five
 * businesses came to read `5, 5, 5, 5, 5`.
 */
export const SORTABLE_FORMATS = ['number', 'percent', 'rating', 'object'];

/** Which columns can be sorted — the numeric ones, plus name. */
export function sortableColumns(columns) {
  return columns.filter((c) => SORTABLE_FORMATS.includes(c.format));
}

/**
 * The table.
 *
 * ## Two tables, not one
 *
 * The header row and the body are rendered as **two separate `<table>` elements**: a sticky one
 * outside the scroller, and the scrolled one below it. They look like one table because the
 * column widths are locked to the same values — see `columnWidths` — and the header is shifted
 * horizontally by exactly the scroller's `scrollLeft`.
 *
 * This is not a preference. A `position: sticky` element sticks to its nearest **scrolling
 * ancestor**, and the body is inside `overflow-x: auto`, which is a scroll container on both
 * axes (CSS requires the pairing; `overflow-y: visible` is computed back to `auto`). So a sticky
 * `<th>` inside it sticks to a container that never scrolls vertically and simply travels off the
 * top of the page. Verified in a minimal reproduction: scrolled 1,100px, the header was 99px above
 * the viewport.
 *
 * Two tables with locked widths is the standard escape from that, and it buys two other things:
 * the header can be `position: sticky` against the **viewport**, and the first column can be
 * `position: sticky` against the **scroller** — which is what lets a 20-column table be read
 * without losing track of which business each row belongs to.
 *
 * ## The indicator and the header share ONE sticky box, above the body
 *
 * Three separate sticky elements cannot stack. Each occupies its own space in the flow and sticks
 * within *that* space, so they cannot overlap: order them one way and the lower one scrolls away,
 * order them the other and the upper one slides under. Both orderings were built and measured, and
 * both failed — the indicator scrolled off, or the header did.
 *
 * So they are one `position: sticky` box holding the indicator and the header table in visual
 * order. One sticky element, two rows inside it, no arithmetic about which `top` belongs to
 * which, and nothing that can drift when a row's height changes.
 *
 * It sits above the body in the source, so it also occupies the flow position the header belongs
 * in — no negative margins, and the body starts exactly where the reader expects it to.
 *
 * The body table keeps a `visually-hidden` `<thead>` with real `<th scope="col">`, so the column
 * semantics a screen reader needs still exist and still describe the cells below them. The visible
 * header is a second, purely visual copy, `aria-hidden` so every column is not announced twice.
 *
 * ## Why the widths are locked, and why they are in the markup
 *
 * Two tables only look like one table if their columns line up, and browsers distribute
 * auto-layout widths from content — so two independently laid-out tables with the same labels
 * will *not* line up. `columnWidths` measures the body once and writes explicit `<colgroup>`
 * widths onto both.
 *
 * The alternative, a single table, is what the design had before this and it cannot stick. The
 * alternative of letting the two tables auto-size independently produces a header whose columns
 * do not match the data under them, which is worse than either.
 *
 * `colgroup` rather than per-cell widths: it is one place to change, and it cannot drift between
 * the header and the body.
 *
 * Real `<th scope>` on both axes, and the anchor marked in **text** (`your business`) rather than
 * by colour alone.
 *
 * The `.fam-scroll-host` is where the scroll indicator is mounted by `compare-app.js`; its markup
 * is deliberately empty because whether a table overflows is a measurement, not something a
 * template can know.
 */
export function renderTable(state, columns) {
  if (state.total === 0) {
    return '<p class="fam-empty">No businesses to compare on this page yet.</p>';
  }

  const head = columns
    .map((c) => {
      // `escapeAttribute`, not `escapeHtml`: the hint is inside a quoted attribute, and a
      // business-derived hint containing `"` would otherwise close it and inject markup.
      const hint = c.hint ? ` title="${escapeAttribute(c.hint)}"` : '';
      return `<th scope="col" class="fam-th" style="width:${c.width}px"${hint}><span class="fam-th-label">${escapeHtml(c.label)}</span><span class="visually-hidden">${escapeHtml(c.sectionTitle)}</span></th>`;
    })
    .join('');

  /**
   * The same headers again, as `<div>`s rather than `<th>`s.
   *
   * The visible header is the one inside the shifting cell, and a `<th>` there would be invalid:
   * a `<th>` may only be a child of `<tr>`, so `<div><th></div>` inside a `<td>` makes the HTML
   * parser relocate every one of them out of the cell. The symptom is a header row that renders
   * in the file and is **absent from the DOM** — measured as zero elements matching the
   * selector, with the markup visibly present in the source.
   *
   * So the shifting copy is a flex row of `<div>`s carrying the same text, classes, widths and
   * `title`. It is `aria-hidden` at the container level anyway, so losing `<th>` costs nothing:
   * the semantics live in the body's own `<thead>`, which is a real `<tr>` of real
   * `<th scope="col">` and stays in the accessibility tree.
   */
  const headDivs = columns
    .map((c) => {
      const hint = c.hint ? ` title="${escapeAttribute(c.hint)}"` : '';
      return `<div class="fam-th" style="width:${c.width}px;flex:0 0 ${c.width}px"${hint}><span class="fam-th-label">${escapeHtml(c.label)}</span><span class="visually-hidden">${escapeHtml(c.sectionTitle)}</span></div>`;
    })
    .join('');

  /**
   * The column widths, as one `<colgroup>` shared by both tables.
   *
   * **One `<col>` per column, each with its own width.** Not `span` with a single width: a
   * span applies one width to every column it covers, which is the flat 120px that clipped
   * `4.7` to `5` while crushing a 36-character Place ID into eight wrapped lines.
   *
   * The two tables share this string, which is what keeps them in agreement — and it has to be
   * one string rather than two derived calls, because two calls that agree today are two calls
   * that can disagree tomorrow.
   *
   * `style` rather than a class per width: the widths are data, and a class per distinct width
   * would mean inventing one for every label length on every page.
   */
  const cols = `<col class="fam-col-name">${columns
    .map((c) => `<col style="width:${c.width}px">`)
    .join('')}`;

  /**
   * The width of the header's shifting cell: the sum of every data column.
   *
   * A `<td>` in a two-column table occupies exactly one column of its `<colgroup>` however much
   * is inside it, so this cell has to be told the total — otherwise it covers only the first data
   * column and every header after it is out by the width of the rest.
   */
  const shiftWidth = columns.reduce((sum, c) => sum + c.width, 0);

  const body = state.visible
    .map((row) => {
      const cells = row.cells
        .map((c) => {
          const text = formatCellText(c.state, c.format);
          const reason = cellStateReason(c.state);
          if (!text) {
            return `<td class="fam-td fam-td--${reason ? 'unread' : 'empty'}"><span class="cmp-unread" aria-hidden="true">—</span>${
              reason ? `<span class="visually-hidden"> (${escapeHtml(reason)})</span>` : ''
            }</td>`;
          }
          return `<td class="fam-td">${escapeHtml(text)}</td>`;
        })
        .join('');

      /**
       * The anchor control lives on every row, in the row header cell.
       *
       * An "which business is mine?" chooser elsewhere on the page means the reader has to
       * remember a list of names and match them against a different list. Putting the button on
       * the row makes choosing and comparing the same act — which is why the workspace's single
       * "Set as my business" button, sitting in the detail section above the table, was a worse
       * design even though it worked.
       *
       * It stays inside the `<th scope="row">` rather than in a second `<tr>`: one row per
       * business is the entire premise of this layout, and a second row per business doubles
       * the DOM to carry a 24px button.
       */
      const anchorControl = row.anchor
        ? '<span class="fam-anchor-flag">your business</span>'
        : row.placeId
          ? `<button type="button" class="fam-anchor-btn" data-anchor-place="${escapeAttribute(row.placeId)}">Mark as mine</button>`
          : '<span class="fam-anchor-none">no Place ID</span>';

      return `<tr class="fam-tr${row.anchor ? ' fam-tr--anchor' : ''}">
        <th scope="row" class="fam-name">
          <span class="fam-name-line">
            ${row.anchor ? '<span class="fam-anchor-marker" aria-hidden="true">◆</span>' : ''}
            <span class="fam-name-text">${escapeHtml(row.name)}</span>
          </span>
          <span class="fam-name-actions">${anchorControl}</span>
        </th>${cells}
      </tr>`;
    })
    .join('');

  return `<div class="fam-scroll-frame" style="--fam-col-count:${columns.length};--fam-col-width:${200 + shiftWidth}px">
    <div class="fam-table-top">
      <div class="fam-scroll-host"></div>
      <div class="fam-table-head-clip" aria-hidden="true">
        <table class="fam-table fam-table--head">
          <colgroup><col class="fam-col-name"><col style="width:${shiftWidth}px"></colgroup>
          <thead>
            <tr>
              <th scope="col" class="fam-name fam-name--head">Business</th>
              <!--
                The shifting region is a cell, not the row or the table.

                It has to exclude the first column, because a transform on an ancestor moves
                every descendant including a position:sticky one: sticky offsets resolve
                against the *transformed* containing block, so left: 0 on the name cell would
                land it at the shifted edge instead of the viewport edge. Measured at 658px of
                scroll: the header's "Business" cell sat at -621px, off the left of the screen.

                A cell is the narrowest element that can carry the shift without dragging the
                sticky one along with it. Its own width is set in CSS to exactly
                columns x column width, and the headers inside it are a flex row of that same
                width each — so this one cell lines up with all N body columns instead of
                occupying just the first of them.
              -->
              <td class="fam-head-shift"><div class="fam-head-row">${headDivs}</div></td>
            </tr>
          </thead>
        </table>
      </div>
    </div>
    <div class="table-wrapper">
      <table class="fam-table">
        <caption class="visually-hidden">${escapeHtml(state.caption ?? 'Comparison of imported businesses')}</caption>
        <colgroup>${cols}</colgroup>
        <thead class="visually-hidden"><tr><th scope="col">Business</th>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  </div>`;
}

/**
 * The "showing 1–25 of 137" line and the pager.
 *
 * ## The count is never omitted
 *
 * A page that renders 25 rows and says nothing about the other 112 is indistinguishable from a
 * page that has 25. So the count is always present, including the "all N are shown" case, so a
 * reader learns to trust it when it matters.
 */
export function renderPager(state) {
  const parts = [];
  // The pin is only worth mentioning when a filter is doing the selecting. With no filter every
  // business is listed anyway and "1 pinned" is noise; with one, it explains an extra row the
  // reader did not ask for — so "pinned" appears exactly when a filter narrowed the list and
  // the anchor still came along.
  if (state.pinned > 0 && state.filtered) {
    parts.push(`${state.pinned} pinned`);
  }

  const range =
    state.total === 0
      ? 'nothing to show'
      : state.pageCount === 1
        ? `all ${state.total} ${state.total === 1 ? 'business' : 'businesses'}`
        : `showing ${state.firstShown}–${state.lastShown} of ${state.total}`;

  const buttons =
    state.pageCount <= 1
      ? ''
      : `<span class="fam-pager-buttons">
          <button type="button" class="btn btn-secondary fam-page" data-page="${state.page - 1}"${state.page <= 1 ? ' disabled' : ''}>Previous</button>
          <span class="fam-pager-state">Page ${state.page} of ${state.pageCount}</span>
          <button type="button" class="btn btn-secondary fam-page" data-page="${state.page + 1}"${state.page >= state.pageCount ? ' disabled' : ''}>Next</button>
        </span>`;

  return `<div class="fam-pager">
    <p class="fam-count" role="status" aria-live="polite">${escapeHtml(range)}${parts.length > 0 ? ` · ${escapeHtml(parts.join(' · '))}` : ''}</p>
    ${buttons}
  </div>`;
}

/**
 * A leader board beside the table, so the "across" view is not lost to the transposed layout.
 *
 * Only for columns where "highest" is a real claim — `better` is `'none'` for text fields
 * precisely because a competitor having a phone number is a fact, not a ranking.
 */
export function renderLeaders(rows, columns, limit = 10) {
  const ranked = sortableColumns(columns).filter((c) => c.better === 'higher' || c.better === 'lower');

  if (ranked.length === 0) {return '';}

  const blocks = ranked
    .map((c) => {
      const entries = rows
        .map((r) => ({ row: r, cell: r.cells.find((x) => x.key === c.key) }))
        .filter((e) => numericFor(e.row, c.key) !== null)
        .sort((a, b) => numericFor(b.row, c.key) - numericFor(a.row, c.key))
        .slice(0, limit);

      if (entries.length < 2) {return '';}

      const items = entries
        .map((e) => {
          // The number, not the cell's text. See `leaderValue` for why these differ.
          const text = leaderValue(e.row, c);
          const marker = e.row.anchor ? '<span class="visually-hidden">(your business) </span>' : '';
          return `<li class="fam-lead-item">${marker}<span class="fam-lead-name">${escapeHtml(e.row.name)}</span><span class="fam-lead-value">${escapeHtml(text)}</span></li>`;
        })
        .join('');

      return `<section class="fam-leader">
        <h3 class="fam-leader-title">${escapeHtml(c.label)}${c.better === 'lower' ? ' — fewest first' : ' — most first'}</h3>
        <ol class="fam-lead-list">${items}</ol>
      </section>`;
    })
    .filter(Boolean)
    .join('');

  return blocks === '' ? '' : `<div class="fam-leaders">${blocks}</div>`;
}